import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';
import { useScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { type OnArrowClick } from 'dibs-scroll-carousel/exports/types.ts';

import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import {
    trackArrowClick,
    trackViewRoomDisplayed,
} from '../../respShared/PdpRespSharedHeroCarousel/tracking.ts';
import { type CarouselItem } from './types.ts';
import { computeThumbMetrics, SRC_SET_WIDTHS, THUMBS_VISIBLE } from './constants.ts';

type ThumbsPanelReturn = {
    thumbsScrollRef: RefObject<HTMLDivElement | null>;
    canScrollUp: boolean;
    canScrollDown: boolean;
    scrollThumbsUp: () => void;
    scrollThumbsDown: () => void;
    hasOverflow: boolean;
    handleThumbSelect: (index: number) => void;
    onArrowClick: OnArrowClick;
};

/**
 * Manages the vertical thumbnails panel that acts as a secondary controller
 * for the main hero carousel. Handles scroll position syncing and thumb click
 * navigation.
 */
export function useThumbsPanel({
    carouselItems,
    afterDisplay,
}: {
    carouselItems: CarouselItem[];
    afterDisplay: boolean;
}): ThumbsPanelReturn {
    const { activeSlide, totalSlides, setCurrentIndex } = useScrollCarousel();
    const slideChangeTypeRef = useRef<'next' | 'previous' | 'other'>('other');
    const thumbsScrollRef = useRef<HTMLDivElement>(null);
    const thumbMetricsRef = useRef(computeThumbMetrics());
    const preloadedUrlsRef = useRef<Set<string>>(new Set());

    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(true);
    const [hasOverflow, setHasOverflow] = useState(false);

    const onArrowClick: OnArrowClick = useCallback(({ direction }) => {
        slideChangeTypeRef.current = direction;
    }, []);

    const syncThumbMetrics = useCallback(() => {
        thumbMetricsRef.current = computeThumbMetrics();
    }, []);

    const [debouncedSyncThumbMetrics] = useDebouncedCallback(syncThumbMetrics, 150);

    useEffect(() => {
        if (!afterDisplay) {
            return undefined;
        }

        syncThumbMetrics();
        window.addEventListener('resize', debouncedSyncThumbMetrics);

        return () => {
            window.removeEventListener('resize', debouncedSyncThumbMetrics);
        };
    }, [afterDisplay, syncThumbMetrics, debouncedSyncThumbMetrics]);

    useEffect(() => {
        const currentItem = carouselItems[activeSlide - 1];
        if (slideChangeTypeRef.current === 'next' || slideChangeTypeRef.current === 'previous') {
            trackArrowClick({
                slideType: currentItem.__type,
                activeSlide,
                totalSlides,
                arrowDirection: slideChangeTypeRef.current,
            });

            const el = thumbsScrollRef.current;
            const { thumbSize, thumbItemHeight, pageHeight } = thumbMetricsRef.current;
            if (el && thumbItemHeight > 0) {
                const thumbTop = (activeSlide - 1) * thumbItemHeight;
                const thumbBottom = thumbTop + thumbSize;

                if (thumbBottom > el.scrollTop + el.clientHeight || thumbTop < el.scrollTop) {
                    el.scrollTo({
                        top: Math.floor(thumbTop / pageHeight) * pageHeight,
                        behavior: 'smooth',
                    });
                }
            }
        }

        if (currentItem.__type === 'viewInRoom') {
            trackViewRoomDisplayed({ activeSlide, totalSlides });
        }

        slideChangeTypeRef.current = 'other';
    }, [activeSlide, totalSlides, carouselItems]);

    const updateScrollArrows = useCallback(() => {
        const el = thumbsScrollRef.current;
        if (!el) return;
        setCanScrollUp(el.scrollTop > 0);
        setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
        setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    }, []);

    useEffect(() => {
        const el = thumbsScrollRef.current;
        if (!el) {
            return undefined;
        }
        updateScrollArrows();
        el.addEventListener('scroll', updateScrollArrows, { passive: true });
        return () => {
            el.removeEventListener('scroll', updateScrollArrows);
        };
    }, [updateScrollArrows, afterDisplay, carouselItems.length]);

    const preloadUrl = useCallback((url: string) => {
        if (!preloadedUrlsRef.current.has(url)) {
            const dpr = window.devicePixelRatio || 1;
            const width = dpr > 1 ? SRC_SET_WIDTHS[1] : SRC_SET_WIDTHS[0];
            const cdnUrl = getSrcsetUrl(url, width);
            const img = new window.Image();
            img.decoding = 'async';
            img.src = cdnUrl;
            preloadedUrlsRef.current.add(url);
        }
    }, []);

    const handleThumbSelect = useCallback(
        (index: number) => {
            const bfCount = Math.min(carouselItems.length, THUMBS_VISIBLE);
            setCurrentIndex({
                index: index + bfCount,
                backfillStartCount: bfCount,
            });
        },
        [carouselItems.length, setCurrentIndex]
    );

    const scrollThumbsByPage = useCallback((direction: 'up' | 'down') => {
        const el = thumbsScrollRef.current;
        if (!el) {
            return;
        }
        const { pageHeight } = thumbMetricsRef.current;
        const scrollDistance = pageHeight > 0 ? pageHeight : el.clientHeight;
        el.scrollBy({
            top: direction === 'up' ? -scrollDistance : scrollDistance,
            behavior: 'smooth',
        });
    }, []);

    const scrollThumbsUp = useCallback(() => {
        scrollThumbsByPage('up');
    }, [scrollThumbsByPage]);

    const scrollThumbsDown = useCallback(() => {
        scrollThumbsByPage('down');
    }, [scrollThumbsByPage]);

    useEffect(() => {
        if (!afterDisplay) {
            return undefined;
        }

        const preloadPhoto = (carouselItem: CarouselItem): void => {
            if (carouselItem.__type === 'photo' && carouselItem.masterOrZoomPath) {
                preloadUrl(carouselItem.masterOrZoomPath);
            }
        };

        carouselItems.slice(0, THUMBS_VISIBLE).forEach(preloadPhoto);

        const remaining = carouselItems.slice(THUMBS_VISIBLE);
        if (remaining.length === 0) {
            return undefined;
        }

        const schedule = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 200));
        const cancel = window.cancelIdleCallback ?? clearTimeout;
        const id = schedule(() => remaining.forEach(preloadPhoto));

        return () => cancel(id);
    }, [afterDisplay, carouselItems, preloadUrl]);

    return {
        thumbsScrollRef,
        canScrollUp,
        canScrollDown,
        scrollThumbsUp,
        scrollThumbsDown,
        hasOverflow,
        handleThumbSelect,
        onArrowClick,
    };
}
